import { g } from "analytics";
import * as React from "react";
import { Badge } from "tiers/badge";
import { meets, TierContext } from "tiers/context";
import { countryCodeToCurrency } from "tiers/currency";
import { PlusPromoModal } from "tiers/promo";
import { useLocation, useRoute } from "wouter";
import { usePricingSeo } from "./seo";
const MAX_WAIT_FOR_CURRENCY_MS = 250;
/** Provides the promo popup as well as various functions to work with tiers. */
export const TierContextProvider = ({ auth, tier: tier, children }) => {
    const [isPricingRoute] = useRoute("/pricing");
    const [location, setLocation] = useLocation();
    const [isPromoOpen, setIsPromoOpen] = React.useState(false);
    const [analyticsTag, setAnalyticsTag] = React.useState();
    const currencyPromiseRef = React.useRef(null);
    // Default to undefined; PlansGrid defaults to "usd" until resolved
    const [defaultCurrency, setDefaultCurrency] = React.useState();
    // Dynamic title & meta description for /pricing
    usePricingSeo(Boolean(isPricingRoute));
    const fetchCurrency = React.useCallback(() => {
        if (defaultCurrency) {
            return Promise.resolve(defaultCurrency);
        }
        if (currencyPromiseRef.current) {
            return currencyPromiseRef.current;
        }
        currencyPromiseRef.current = fetch("https://ipinfo.io/json?token=a91f57b3b0b7ec")
            .then(res => {
            if (!res.ok) {
                return undefined;
            }
            return res.json();
        })
            .then(data => {
            const countryCode = data === null || data === void 0 ? void 0 : data.country;
            if (countryCode) {
                const selected = countryCodeToCurrency(countryCode);
                setDefaultCurrency(selected);
                return selected;
            }
            return undefined;
        })
            .catch(() => undefined);
        return currencyPromiseRef.current;
    }, [defaultCurrency]);
    // Fetch default currency when navigating directly to /pricing
    React.useEffect(() => {
        if (isPricingRoute && !defaultCurrency) {
            fetchCurrency();
        }
    }, [isPricingRoute, defaultCurrency, fetchCurrency]);
    // Handle legacy #pricing and #chemix URL fragments
    React.useEffect(() => {
        if (window.location.hash === "#pricing" || window.location.hash === "#chemix") {
            setLocation("/pricing");
        }
    }, [setLocation]);
    const allow = React.useCallback((itemOrTier) => {
        if (typeof itemOrTier === "string") {
            return meets(itemOrTier, tier);
        }
        else {
            // If the item does not have a tier, it's considered basic.
            const itemTier = itemOrTier.tier;
            if (!itemTier)
                return true;
            return meets(itemTier, tier);
        }
    }, [tier]);
    const handleClose = React.useCallback((open) => {
        setIsPromoOpen(open);
        if (!open && isPricingRoute) {
            setLocation("/");
        }
    }, [isPricingRoute, setLocation]);
    const popup = React.useCallback((analyticsLabel) => {
        g("PromoPopup", analyticsLabel !== null && analyticsLabel !== void 0 ? analyticsLabel : "");
        setAnalyticsTag(analyticsLabel);
        const open = () => {
            if (location !== "/pricing") {
                setLocation("/pricing");
            }
            else {
                setIsPromoOpen(true);
            }
        };
        if (defaultCurrency) {
            // Currency already fetched. Open immediately
            open();
        }
        else {
            let hasOpened = false;
            let timeoutId;
            const openOnce = () => {
                if (!hasOpened) {
                    hasOpened = true;
                    if (timeoutId !== undefined) {
                        window.clearTimeout(timeoutId);
                    }
                    open();
                }
            };
            fetchCurrency().then(openOnce);
            timeoutId = window.setTimeout(openOnce, MAX_WAIT_FOR_CURRENCY_MS);
        }
    }, [location, setLocation, defaultCurrency, fetchCurrency]);
    const contextValue = React.useMemo(() => {
        return {
            tier,
            allow,
            badge: (itemOrTier, extraClass, opts) => {
                if (allow(itemOrTier)) {
                    return null;
                }
                if (typeof itemOrTier === "string") {
                    return React.createElement(Badge, { tier: itemOrTier, extraClass: extraClass, style: opts === null || opts === void 0 ? void 0 : opts.style });
                }
                else {
                    const itemTier = itemOrTier.tier;
                    if (!itemTier)
                        return null;
                    return React.createElement(Badge, { tier: itemTier, extraClass: extraClass, style: opts === null || opts === void 0 ? void 0 : opts.style });
                }
            },
            popup,
        };
    }, [tier, allow, popup]);
    const showPromoModal = isPromoOpen || Boolean(isPricingRoute);
    return React.createElement(TierContext.Provider, { value: contextValue },
        React.createElement(PlusPromoModal, { auth: auth, tier: tier, isOpen: showPromoModal, setIsOpen: handleClose, analyticsTag: analyticsTag, defaultCurrency: defaultCurrency }),
        children);
};
